import apiClient, { apiRequest } from "./apiClient";

/**
 * AI Workflow Builder service.
 *
 * Two-stage flow (see backend packages/orbit_core/modules/ai_workflow):
 *   1. plan    — natural language -> a structured, reviewable WorkflowPlan
 *   2. compile — an approved plan -> a validated WorkflowDefinition (+ warnings)
 *
 * The compiled definition is then handed to workflowService.createWorkflow to
 * persist it. Nothing is written until the user approves the plan.
 *
 * Both calls hit Gemini, so they override the apiClient's default 10s timeout —
 * planning (with model thinking) can comfortably exceed it.
 */

const PLAN_TIMEOUT_MS = 90_000;
const COMPILE_TIMEOUT_MS = 30_000;

/**
 * Turn a natural-language description into a structured workflow plan.
 * @param {{prompt: string, history?: Array<{role: "user"|"assistant", content: string}>}} args
 * @returns {Promise<{success: boolean, data?: {plan: Object, assistant_message: string}, message?: string}>}
 */
export const planWorkflow = async ({ prompt, history = [] }) =>
  apiRequest(
    () =>
      apiClient.post(
        "/api/v1/ai-workflow/plan",
        { prompt, history },
        { timeout: PLAN_TIMEOUT_MS }
      ),
    "The AI planner couldn't build a plan. Please try again."
  );

/**
 * Compile an approved plan into a runnable WorkflowDefinition. Validates the
 * plan against the org's catalog; returns warnings for non-fatal issues and a
 * 400 (surfaced as message) for fatal ones (hallucinated endpoint, dangling ref).
 * @param {Object} plan - a WorkflowPlan as returned by planWorkflow
 * @returns {Promise<{success: boolean, data?: {definition: Object, warnings: string[]}, message?: string}>}
 */
export const compileWorkflow = async (plan) =>
  apiRequest(
    () =>
      apiClient.post(
        "/api/v1/ai-workflow/compile",
        { plan },
        { timeout: COMPILE_TIMEOUT_MS }
      ),
    "Couldn't compile the plan into a workflow."
  );
